import { fetchWithAuth } from './adminApi'

/** Backstage moderation queues served by the admin API under /admin/backstage. */

export interface ReportedPost {
  id: string
  post_id: string
  reporter_id: string
  reason: string
  created_at: string
  backstage_posts: {
    id: string
    shop_id: string
    title: string | null
    body: string | null
    post_type: string
    visibility: string | null
    status: string
  } | null
}

export interface ReportedComment {
  id: string
  comment_id: string
  reporter_id: string
  reason: string
  created_at: string
  backstage_comments: {
    id: string
    post_id: string
    author_id: string
    body: string
    status: string
  } | null
}

export interface HeldComment {
  id: string
  post_id: string
  author_id: string
  body: string
  created_at: string
}

export interface HeldFile {
  id: string
  shop_id: string
  owner_id: string
  filename: string
  mime_type: string
  size_bytes: number
  created_at: string
}

export type ReportDecision = 'remove' | 'dismiss'
export type ReviewDecision = 'approve' | 'remove'

async function call<T>(path: string, body?: unknown): Promise<T> {
  const result = await fetchWithAuth<T>(
    `/admin/backstage${path}`,
    body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) }
  )
  if (!result.ok || result.data === undefined)
    throw new Error(result.error?.message || 'Request failed')
  return result.data
}

export const backstageModeration = {
  postReports: () => call<ReportedPost[]>('/post-reports'),
  commentReports: () => call<ReportedComment[]>('/comment-reports'),
  heldComments: () => call<HeldComment[]>('/comments/review'),
  heldFiles: () => call<HeldFile[]>('/private-assets/review'),
  decidePost: (postId: string, action: ReportDecision) =>
    call(`/post-reports/${encodeURIComponent(postId)}/decision`, { action }),
  decideReportedComment: (commentId: string, action: ReportDecision) =>
    call(`/comment-reports/${encodeURIComponent(commentId)}/decision`, { action }),
  decideHeldComment: (commentId: string, action: ReviewDecision) =>
    call(`/comments/${encodeURIComponent(commentId)}/decision`, { action }),
  decideFile: (assetId: string, action: ReviewDecision) =>
    call(`/private-assets/${encodeURIComponent(assetId)}/decision`, { action }),
  /** Short-lived signed link to a held file. The storage client has used both key spellings. */
  previewFile: async (assetId: string) => {
    const link = await call<{ signedURL?: string; signedUrl?: string }>(
      `/private-assets/${encodeURIComponent(assetId)}/preview`,
      {}
    )
    const url = link.signedURL || link.signedUrl
    if (!url) throw new Error('No preview link returned')
    return url
  },
}
